import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { collectBattlefieldSlotCandidates } from '../utils/mobileBattleDamageFx';
import './HealthCountdownFlyout.css';

/** Minimum ms per countdown step so each flyout can grow and dissipate before the next tick. */
export const HEALTH_FLYOUT_MIN_STEP_MS = 260;

function escapeSelectorValue(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(String(value));
  }
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function collectAnchorCandidates({ instanceId, zoneName, slotIndex, mobileLayout = false }) {
  const candidates = [];

  if (zoneName != null && slotIndex != null) {
    const slots = mobileLayout
      ? collectBattlefieldSlotCandidates(zoneName, slotIndex)
      : [
          document.querySelector(
            `[data-slot-zone="${escapeSelectorValue(zoneName)}"][data-slot-index="${String(slotIndex)}"]`
          ),
        ].filter(Boolean);

    for (const slot of slots) {
      candidates.push(
        ...slot.querySelectorAll('[data-health-fx-anchor], .order-health-countdown-anchor')
      );
    }
  }

  if (instanceId != null && instanceId !== '') {
    candidates.push(
      ...document.querySelectorAll(`[data-health-fx-anchor="${escapeSelectorValue(instanceId)}"]`)
    );
  }

  return [...new Set(candidates)];
}

/**
 * Locate the live health badge anchor for a battlefield unit.
 */
export function resolveHealthFxAnchorElement({ instanceId, zoneName, slotIndex, mobileLayout = false }) {
  const candidates = collectAnchorCandidates({ instanceId, zoneName, slotIndex, mobileLayout });
  let best = null;
  let bestArea = 0;

  for (const candidate of candidates) {
    const rect = candidate.getBoundingClientRect();
    const area = rect.width * rect.height;
    if (area > bestArea) {
      bestArea = area;
      best = candidate;
    }
  }

  if (best) {
    return best;
  }

  if (candidates.length > 0) {
    return candidates[0];
  }

  throw new Error(
    `resolveHealthFxAnchorElement: health badge anchor not found (instanceId=${instanceId}, zone=${zoneName}, index=${slotIndex})`
  );
}

/**
 * Prefer the numeric HP text inside the badge — not the icon or outer padding box.
 */
export function resolveHealthFxOriginElement(anchorEl) {
  if (!anchorEl) {
    throw new Error('resolveHealthFxOriginElement: anchorEl is required');
  }

  const inner = anchorEl.querySelector('[data-health-fx-origin], .order-health-countdown-anchor__value');
  if (inner) {
    return inner;
  }

  return anchorEl;
}

function readElementViewportCenter(el) {
  const rect = el.getBoundingClientRect();
  const width = rect.width > 0 ? rect.width : (el.offsetWidth || el.clientWidth);
  const height = rect.height > 0 ? rect.height : (el.offsetHeight || el.clientHeight);
  if (width <= 0 || height <= 0) {
    return null;
  }
  return {
    x: rect.left + width / 2,
    y: rect.top + height / 2,
  };
}

export function measureHealthFxAnchorCenter(anchorEl) {
  if (!anchorEl) {
    throw new Error('measureHealthFxAnchorCenter: anchorEl is required');
  }

  const originEl = resolveHealthFxOriginElement(anchorEl);
  const originCenter = readElementViewportCenter(originEl);
  if (originCenter) {
    return originCenter;
  }

  let el = anchorEl;
  while (el && el !== document.body) {
    const center = readElementViewportCenter(el);
    if (center) {
      return center;
    }
    el = el.parentElement;
  }

  throw new Error('measureHealthFxAnchorCenter: no measurable element in anchor chain');
}

export function resolveHealthFxFlyoutOrigin({ instanceId, zoneName, slotIndex, mobileLayout = false }) {
  const anchorEl = resolveHealthFxAnchorElement({ instanceId, zoneName, slotIndex, mobileLayout });
  return measureHealthFxAnchorCenter(anchorEl);
}

/**
 * Large HP number anchored to the order health badge; scales up and flies toward the player.
 */
export function HealthCountdownFlyout({ value, zoneName, origin, durationMs, mobileLayout = false }) {
  if (typeof value !== 'number' || !origin || typeof document === 'undefined') {
    return null;
  }

  if (typeof durationMs !== 'number' || durationMs <= 0) {
    throw new Error('HealthCountdownFlyout: durationMs is required');
  }

  const fromOpponent = zoneName?.startsWith('opponent_');
  const variantClass = fromOpponent
    ? 'health-countdown-flyout--from-opponent'
    : 'health-countdown-flyout--from-friendly';
  const mobileClass = mobileLayout ? ' health-countdown-flyout--mobile' : '';

  return createPortal(
    <div
      className={`health-countdown-flyout health-countdown-flyout--portal${mobileClass} ${variantClass}`}
      style={{
        left: `${origin.x}px`,
        top: `${origin.y}px`,
        '--health-flyout-duration': `${durationMs}ms`,
      }}
      aria-hidden
    >
      <span className="health-countdown-flyout__ring" />
      <span className="health-countdown-flyout__value">{value}</span>
    </div>,
    document.body
  );
}

/**
 * Renders one flyout pulse, measuring the live badge number position at mount time.
 */
export function HealthCountdownFlyoutFromInstance({
  value,
  zoneName,
  instanceId,
  slotIndex,
  durationMs,
  onComplete,
  mobileLayout = false,
}) {
  const [origin, setOrigin] = useState(null);

  useLayoutEffect(() => {
    if (!instanceId) {
      throw new Error('HealthCountdownFlyoutFromInstance: instanceId is required');
    }
    setOrigin(resolveHealthFxFlyoutOrigin({ instanceId, zoneName, slotIndex, mobileLayout }));
  }, [instanceId, zoneName, slotIndex, value, mobileLayout]);

  useEffect(() => {
    if (typeof onComplete !== 'function' || !origin || typeof durationMs !== 'number') {
      return undefined;
    }
    const timerId = window.setTimeout(onComplete, durationMs);
    return () => window.clearTimeout(timerId);
  }, [onComplete, origin, durationMs, value]);

  if (!origin) {
    return null;
  }

  return (
    <HealthCountdownFlyout
      value={value}
      zoneName={zoneName}
      origin={origin}
      durationMs={durationMs}
      mobileLayout={mobileLayout}
    />
  );
}

export default HealthCountdownFlyout;
